import * as Yup from 'yup';
import type { TestContext } from 'yup';
import i18n from '@/i18n';
import { TranslationNS } from '@gokrew/models/constants';

type MsgOrFn = string | (() => string);
const resolve = (msg: MsgOrFn): string =>
  typeof msg === 'function' ? msg() : msg;
const v =
  (key: string): (() => string) =>
  () =>
    i18n.t(key, { ns: TranslationNS.validations });

interface ValidationParent {
  user?: {
    email?: string;
  };
}

/**
 * A Yup validator that checks if a string contains any characters
 * other than whitespace.
 * @param reqMsg - The error message to display when the validator fails.
 *
 * @todo handle for date type inputs
 */
export function nonEmptyReqValidator(reqMsg: MsgOrFn = v('field_required')) {
  return Yup.string()
    .trim()
    .test('non-empty', (value, ctx) => {
      if (!value || value.trim().length === 0)
        return ctx.createError({ message: resolve(reqMsg) });
      return true;
    });
}

/**
 * A Yup validator that checks if a string contains only whitespace.
 * @param reqMsg - The error message to display when the validator fails.
 *
 * @todo handle for date type inputs
 */
export function nonWhiteSpaceValidator(reqMsg: MsgOrFn = v('no_empty_spaces')) {
  return Yup.string().test('no-whitespace-only', (value, ctx) => {
    if (value == null || value === '') return true; // let required() handle empties elsewhere
    if (value.trim() === value && value.trim().length > 0) return true; // rejects "   "
    return ctx.createError({ message: resolve(reqMsg) });
  });
}

export function strictNoSpaceValidator(reqMsg: MsgOrFn = v('no_empty_spaces')) {
  return Yup.string().test('no-spaces', (value, ctx) => {
    if (value == null || value === '') return true; // Let required() handle empty
    if (!/\s/.test(value)) return true; // Accept only if no whitespace
    return ctx.createError({ message: resolve(reqMsg) });
  });
}

/**
 * Matches a single special character accepted by the password strength rules.
 */
export const passwordSpecialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

/**
 * A Yup validator that checks if a string contains at least one special
 * character (as defined by passwordSpecialCharRegex).
 * @param reqMsg - The error message to display when the validator fails.
 */
export function specialCharValidator(
  reqMsg: MsgOrFn = v('special_char_required')
) {
  return Yup.string().test('special-char', (value, ctx) => {
    if (value == null || value === '') return true; // let required() handle empties elsewhere
    if (passwordSpecialCharRegex.test(value)) return true;
    return ctx.createError({ message: resolve(reqMsg) });
  });
}

/**
 * @todo handle validation of invalid number of dots(.)
 *
 * Local part may start with a letter or digit (e.g. "123abc@example.com" is
 * valid) — only a leading/doubled dot is actually disallowed.
 */
export const emailValidationRegex =
  /^(?![.])(?!.*[.]{2})[A-Za-z0-9][A-Za-z0-9._%+-]*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/**
 * Matches a phone number after normalizePhone() has stripped formatting —
 * digits only, with an optional leading '+', 7-15 digits long.
 */
export const phoneValidationRegex = /^\+?[0-9]{7,15}$/;

/**
 * Strips everything but digits and a leading '+' from a phone number, so
 * naturally-formatted input ("555-123-4567", "+1 (555) 123-4567") can be
 * checked against phoneValidationRegex.
 */
export function normalizePhone(value: string): string {
  const trimmed = value.trim();
  const digits = trimmed.replace(/[^0-9]/g, '');
  return trimmed.startsWith('+') ? `+${digits}` : digits;
}

// Mirrors the limits validator.js's isEmail() enforces on the backend
// (max length 254, local-part max 64) so we surface the same error pre-submit.
const EMAIL_MAX_LENGTH = 254;
const EMAIL_LOCAL_PART_MAX_LENGTH = 64;

/**
 * A Yup validator that checks if a string is a valid email address.
 * @param reqMsg - The error message to display when the validator fails.
 * @param maxLengthMsg - The error message to display when the email exceeds the backend's length limits.
 */
export function emailValidator(
  reqMsg: MsgOrFn = v('invalid_email'),
  maxLengthMsg: MsgOrFn = v('email_too_long')
) {
  return Yup.string().test('email', (value, ctx) => {
    if (!value) return true;
    if (!emailValidationRegex.test(value)) {
      return ctx.createError({ message: resolve(reqMsg) });
    }
    // Regex guarantees exactly one '@', so split is safe here.
    const [localPart] = value.split('@');
    if (
      value.length > EMAIL_MAX_LENGTH ||
      localPart.length > EMAIL_LOCAL_PART_MAX_LENGTH
    ) {
      return ctx.createError({ message: resolve(maxLengthMsg) });
    }
    return true;
  });
}

/**
 * A Yup validator that checks if a string is alphanumeric and contains
 * @param {string} fieldLabel - The label of the field to validate.
 */
export const alphanumericValidator = (fieldLabel: string) =>
  Yup.string().test('alphanumeric', (value, ctx) => {
    if (!value) return true;
    if (/^[\p{L}0-9\s.-]+$/u.test(value)) return true;
    return ctx.createError({
      message: i18n.t('no_special_chars', {
        ns: TranslationNS.validations,
        field: fieldLabel,
      }),
    });
  });

export const requiredAlphanumericValidator = (fieldLabel: string) =>
  nonEmptyReqValidator(() =>
    i18n.t('field_required_label', { ns: 'validations', field: fieldLabel })
  ).test('alphanumeric', (value, ctx) => {
    if (!value) return true;
    if (/^[\p{L}0-9\s.-]+$/u.test(value)) return true;
    return ctx.createError({
      message: i18n.t('no_special_chars', {
        ns: TranslationNS.validations,
        field: fieldLabel,
      }),
    });
  });

//  * Validates that the work email is not the same as the user's personal email.
//  * This comparison is case-insensitive and trims extra spaces.
//  *
//  * @param value - The work email value being validated.
//  * @param context - Yup validation context, which gives access to sibling field values.
//  * @returns true if valid, false if work email matches personal email.
export const isWorkEmailNotSameAsUserEmail = (
  value: string | undefined,
  context: TestContext<ValidationParent>
): boolean => {
  const userEmail = context.parent?.user?.email;
  if (!value || !userEmail) return true;
  return value.trim().toLowerCase() !== userEmail.trim().toLowerCase();
};

/**
 * A Yup validator that checks if a date field is required.
 * @param message - The error message to display when the validator fails.
 * @returns A Yup validator that checks if a date field is required.
 */
export const requiredDateValidator = (
  message: MsgOrFn = v('field_required')
) => {
  return Yup.date().test('required', (value, ctx) => {
    if (value) return true;
    return ctx.createError({ message: resolve(message) });
  });
};

/**
 * A Yup validator that checks if a number field is required.
 *
 * @param message - The error message to display when the validator fails.
 * @returns A Yup validator that checks if a number field is required.
 */
export const requiredNumberValidator = (
  message: MsgOrFn = v('field_required')
) => {
  return Yup.number().test('required', (value, ctx) => {
    if (value) return true;
    return ctx.createError({ message: resolve(message) });
  });
};

/**
 * Returns a Yup date validator that allows for null or empty strings.
 * If the input is an empty string, it will be transformed to null.
 * The validator is optional, meaning it will not fail if the input is undefined.
 */
export const optionalDate = () =>
  Yup.date()
    .nullable()
    .transform((v) => (v === '' ? null : v))
    .optional();

/**
 * Returns a Yup string validator that allows for null or empty strings.
 * If the input is an empty string, it will be transformed to null.
 * The validator is optional, meaning it will not fail if the input is undefined.
 */
export const optionalString = () =>
  Yup.string()
    .nullable()
    .transform((v) => (v === '' ? null : v))
    .optional();

// Case-insensitive, so "WWW.Acme.com" and "HTTPS://acme.ca" pass too.
export const urlValidationRegex =
  /^(https?:\/\/)?(www\.)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i;

/**
 * A Yup validator that checks if a string is a valid URL.
 * Supports URLs with or without http/https and www.
 * @param reqMsg - The error message to display when validation fails.
 */
export function urlValidator(reqMsg: MsgOrFn = v('invalid_url')) {
  return Yup.string().test('url-validation', (value, ctx) => {
    if (!value) return true; // allow null / empty
    if (urlValidationRegex.test(value)) return true;
    return ctx.createError({ message: resolve(reqMsg) });
  });
}

export function nameValidator(invalidMsg: MsgOrFn = v('name_invalid')) {
  return Yup.string().test('safe-name', (value, ctx) => {
    if (value == null || value === '') return true;
    if (!/[<>"`&;{}()=\d]/.test(value)) return true;
    return ctx.createError({ message: resolve(invalidMsg) });
  });
}
